<template>
  <div class="map-wrap" v-if="hasZip">
    <div class="card card-form">
      <div class="card-header">
        <div class="card-title">Location</div>
      </div>
      <div class="card-body">
        <div class="map-frame-wrap">
          <iframe v-if="coords" :key="embedSrc" :src="embedSrc" class="map-frame" title="Client location" loading="lazy"
            referrerpolicy="no-referrer-when-downgrade"></iframe>
          <div v-else class="map-placeholder">{{ statusText }}</div>
        </div>

        <div class="map-foot">
          <span v-if="resolvedLabel" class="map-caption">{{ resolvedLabel }}</span>
          <a v-if="coords" class="map-link" :href="externalHref" target="_blank" rel="noopener">
            Open in Google Maps
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const NOMINATIM_URL = "https://nominatim.openstreetmap.org/search";
const DEBOUNCE_MS = 800;

// Nominatim is rate limited (1 req/sec), so cache every lookup for the session.
const geocodeCache = new Map();

export default {
  name: "AddressMap",
  props: {
    street1: { type: String, default: "" },
    state: { type: String, default: "" },
    zip: { type: String, default: "" },
    country: { type: String, default: "" },
  },
  data() {
    return {
      coords: null,
      resolvedLabel: "",
      searching: false,
      notFound: false,
      debounceId: null,
      requestId: 0,
    };
  },
  computed: {
    hasZip() {
      return !!(this.zip || "").trim();
    },
    // Every part that can move the pin — a change here triggers a new lookup.
    addressKey() {
      return [this.street1, this.state, this.zip, this.country]
        .map((v) => (v || "").trim())
        .join("|")
        .toLowerCase();
    },
    statusText() {
      if (this.searching) return "Looking up location…";
      if (this.notFound) return "No map match for this address.";
      return "Enter a zip/postal code to see the location.";
    },
    // OpenStreetMap's embed needs a bounding box, so pad the point a little.
    embedSrc() {
      if (!this.coords) return "";
      const { lat, lon } = this.coords;
      const pad = 0.01;
      const bbox = [lon - pad, lat - pad, lon + pad, lat + pad].join("%2C");
      return `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${lat}%2C${lon}`;
    },
    externalHref() {
      if (!this.coords) return "#";
      const { lat, lon } = this.coords;
      return `https://www.google.com/maps/search/?api=1&query=${lat},${lon}`;
    },
  },
  watch: {
    addressKey: {
      immediate: true,
      handler() {
        this.scheduleLookup();
      },
    },
  },
  beforeUnmount() {
    clearTimeout(this.debounceId);
  },
  methods: {
    scheduleLookup() {
      clearTimeout(this.debounceId);
      this.debounceId = setTimeout(this.lookup, DEBOUNCE_MS);
    },
    // Narrowest match first, then progressively coarser fallbacks.
    buildQueries() {
      const street = (this.street1 || "").trim();
      const state = (this.state || "").trim();
      const zip = (this.zip || "").trim();
      const country = (this.country || "").trim();
      const queries = [];

      if (street && zip) queries.push({ street, postalcode: zip, state, country });
      if (zip) queries.push({ postalcode: zip, country });
      if (street) queries.push({ street, state, country });
      if (state && country) queries.push({ state, country });

      return queries.map((q) => {
        const params = new URLSearchParams({ format: "jsonv2", limit: "1", addressdetails: "0" });
        Object.keys(q).forEach((k) => {
          if (q[k]) params.append(k, q[k]);
        });
        return params.toString();
      });
    },
    async lookup() {
      const queries = this.buildQueries();
      if (!queries.length) {
        this.coords = null;
        this.notFound = false;
        return;
      }

      const requestId = ++this.requestId;
      this.searching = true;
      this.notFound = false;

      for (const query of queries) {
        let hit = geocodeCache.get(query);

        if (hit === undefined) {
          hit = await this.geocode(query);
          geocodeCache.set(query, hit);
        }

        // A newer lookup started while this one was in flight — drop the result.
        if (requestId !== this.requestId) return;

        if (hit) {
          this.coords = { lat: hit.lat, lon: hit.lon };
          this.resolvedLabel = hit.label;
          this.searching = false;
          return;
        }
      }

      this.coords = null;
      this.resolvedLabel = "";
      this.searching = false;
      this.notFound = true;
    },
    async geocode(query) {
      try {
        const res = await fetch(`${NOMINATIM_URL}?${query}`, {
          headers: { Accept: "application/json" },
        });
        if (!res.ok) return null;

        const data = await res.json();
        if (!Array.isArray(data) || !data.length) return null;

        const lat = parseFloat(data[0].lat);
        const lon = parseFloat(data[0].lon);
        if (Number.isNaN(lat) || Number.isNaN(lon)) return null;

        return { lat, lon, label: data[0].display_name || "" };
      } catch (err) {
        console.log(err);
        return null;
      }
    },
  },
};
</script>

<style scoped>
/* Mirrors the client form card so the two sit side by side as a pair. */
.map-wrap {
  --teal: #18798c;
  --panel: #f0f1f2;
  --ink: #2c3038;
  --muted: #8a909a;
  --line: #d9dde2;
  --radius: 14px;
  --shadow: 0 6px 24px rgba(20, 30, 50, 0.08);

  display: flex;
  width: 100%;
  max-width: 520px;
  color: var(--ink);
}

.map-wrap .card {
  background: transparent;
  border-radius: var(--radius);
  box-shadow: none;
  overflow: visible;
  border: none;
  flex: 1;
  min-width: 0;
}

.map-wrap .card-header {
  background: transparent;
  border: none;
  padding: 0;
  display: flex;
  justify-content: flex-start;
}

.map-wrap .card-title {
  background: var(--teal);
  color: #fff;
  height: 55px;
  margin: 0;
  padding: 8px 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 700;
  border-radius: 8px 8px 0 0;
  width: fit-content;
  min-width: 140px;
}

.map-wrap .card-body {
  background: var(--panel);
  border-radius: 0 8px 8px 8px;
  box-shadow: var(--shadow);
  padding: 20px;
}

/* Square, so the map scales with the card width instead of running tall. */
.map-frame-wrap {
  border: 1px solid var(--line);
  border-radius: 8px;
  background: #fff;
  overflow: hidden;
  aspect-ratio: 1 / 1;
}

.map-frame {
  display: block;
  width: 100%;
  height: 100%;
  border: 0;
}

.map-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 0 16px;
  text-align: center;
  font-size: 13px;
  color: var(--muted);
}

.map-foot {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  min-height: 18px;
  margin-top: 10px;
}

.map-caption {
  flex: 1;
  min-width: 0;
  font-size: 11px;
  line-height: 1.4;
  color: var(--muted);
}

.map-link {
  flex: none;
  margin-left: auto;
  font-size: 12px;
  font-weight: 600;
  color: var(--teal);
  text-decoration: none;
  white-space: nowrap;
}

.map-link:hover {
  text-decoration: underline;
}

@media (max-width: 991px) {
  .map-wrap {
    max-width: 560px;
  }
}

@media (max-width: 540px) {
  .map-wrap .card-body {
    padding: 16px;
  }

  .map-wrap .card-title {
    height: 48px;
    font-size: 16px;
    padding: 8px 20px;
  }
}
</style>
